import React, { useRef } from "react";
import BaseModal from "../../../components/ui/BaseModal";
import BaseButton from "../../../components/ui/BaseButton";
import WorkExperienceForm from "../../WorkOrder/Forms/WorkExperienceForm";
import { useSelector, useDispatch } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";
import { useUpdateWorkOrder } from "../../../hooks/useUpdateWorkOrder";
import { updateWorkOrderApi } from "../../../services/api/workOrder";
import { useMutation } from "@tanstack/react-query";
import { useSectionChanges } from "../../../hooks/useSectionChanges";
import {
  buildValidationMessage,
  focusFirstInvalidField,
  normalizeValidationResult,
} from "../../../utils/formErrors";
import { useResetValidationOnClose } from "../../../hooks/useResetValidationOnClose";

const VALIDATED_FIELDS = ["categoryId", "vendorId", "workOrderServices"];

const hasSaveableWorkExperience = (workExperience) =>
  !!workExperience?.categoryId &&
  !!workExperience?.vendorId &&
  !!workExperience?.workOrderServices?.length;

const EditWorkExperienceModal = ({ open, close, workExperienceData }) => {
  const dispatch = useDispatch();
  const formRef = useRef();
  const id = useSelector((state) => {
    return state.workOrder.workOrderDetails.id;
  });
  const { updateWorkOrder } = useUpdateWorkOrder();

  const workExperience = useSelector((state) => state.workOrder.summary.workExperience);
  const { hasChanges, onHydrate } = useSectionChanges(workExperience);
  const canSave = hasSaveableWorkExperience(workExperience);
  useResetValidationOnClose(VALIDATED_FIELDS);
  const {
    isPending: isUpdatingLoading,
    error: isUpdatingError,
    mutateAsync: updateWorkOrderApiMutation,
  } = useMutation({
    mutationFn: updateWorkOrderApi,
    onSuccess: (data) => {},
  });

  const handleSubmit = async () => {
    const { valid, errors } = normalizeValidationResult(formRef.current?.isValid?.());

    if (!valid) {
      const message = buildValidationMessage(errors);
      if (message) {
        dispatch(showToast({ message, type: "error" }));
      }
      focusFirstInvalidField();
      return;
    }

    try {
      const formattedWorkExperience = {
        ...workExperience,
        workOrderServices: workExperience?.workOrderServices?.map((service) => ({
          serviceId: service,
        })),
      };

      await updateWorkOrderApiMutation({
        id,
        ...formattedWorkExperience,
      });

      await updateWorkOrder();

      dispatch(
        showToast({
          message: "Work experience updated successfully!",
          type: "success",
        }),
      );

      close();
    } catch (error) {
      console.error(error);
      dispatch(
        showToast({
          message: "Failed to update work experience.",
          type: "error",
        }),
      );
    }
  };

  return (
    <BaseModal className="-top-[200px] overflow-auto" open={open} close={close}>
      <div className="py-7 px-12 xl:w-[1400px] lg:w-[1000px] sm:w-[800px]">
        <h6 className="text-dark font-normal border-b pb-1">Edit Work Experience</h6>

        <div className="mb-2 mt-3">
          <WorkExperienceForm
            ref={formRef}
            workExperienceData={workExperienceData}
            id={id}
            onHydrate={onHydrate}
          />
        </div>
      </div>

      <div
        className="d-flex content-center bg-white py-6"
        style={{
          boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)",
        }}
      >
        <BaseButton
          className="px-[28px] py-[5px] bg-light text-primary hover:!text-primary"
          variant="outlined"
          color="primary"
          style={{ marginRight: "16px" }}
          onClick={() => close(false)}
        >
          Cancel
        </BaseButton>

        <BaseButton
          className="px-[12px] py-[5px] min-w-32"
          color="primary"
          onClick={handleSubmit}
          disabled={!hasChanges || !canSave || isUpdatingLoading}
        >
          Save Changes
        </BaseButton>
      </div>
    </BaseModal>
  );
};

export default EditWorkExperienceModal;
